import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { erp } from "@/lib/erp";
import type { SalesOrder } from "@/lib/erp/types/order";
import { getQueryOptions } from "@/lib/config/queryConfig";
import { useNavigate } from "react-router-dom";
import { routes } from "@/lib/config/routes";
import { computeDirtyState } from "@/features/inventory/item-detail/utils/computeDirtyState";
import { getERPClient } from "@/lib/erp/client";

/**
 * Hook for fetching a single Sales Order detail
 * 
 * @param name - Sales Order name
 * @returns React Query result with Sales Order data
 */
export function useSalesOrderDetail(name: string) {
  return useQuery<SalesOrder>({
    queryKey: ["sales-order", name],
    queryFn: () => erp.orders.detail(name),
    enabled: !!name,
    ...getQueryOptions("detail"),
  });
}

/**
 * Hook for updating an existing sales order
 * 
 * Computes diff payload internally (only changed fields) to minimize network traffic.
 * 
 * @param name - Sales Order name to update
 * @param originalData - Original data from API (used to compute diff)
 * @returns React Query mutation for saving sales order
 */
export function useSaveSalesOrder(name: string, originalData: Record<string, any>) {
  const queryClient = useQueryClient();

  return useMutation<SalesOrder, Error, Record<string, any>>({
    mutationFn: (currentData: Record<string, any>) => {
      // Compute diff INSIDE hook - only send changed fields
      const changes = computeDirtyState(originalData, currentData);
      
      // If no changes, return original data (no API call needed)
      if (Object.keys(changes).length === 0) {
        return Promise.resolve(originalData as SalesOrder);
      }
      
      // Send only changed fields to server
      return erp.orders.update(name, changes);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sales-order", name] });
    },
  });
}

/**
 * Hook for creating a new sales order
 * 
 * @returns React Query mutation for creating sales order
 */
export function useCreateSalesOrder() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation<SalesOrder, Error, Record<string, any>>({
    mutationFn: (data: Record<string, any>) => {
      return erp.orders.create(data);
    },
    onSuccess: (data) => {
      // Invalidate list queries
      queryClient.invalidateQueries({ queryKey: ["sales-orders", "list"] });
      // Redirect to detail page using route config
      if (data.name) {
        navigate(routes.salesOrder.detail(data.name));
      }
    },
  });
}

/**
 * Hook for deleting a sales order
 * 
 * @param name - Sales Order name to delete
 * @returns React Query mutation for deleting sales order
 */
export function useDeleteSalesOrder(name: string) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation<void, Error, void>({
    mutationFn: () => {
      return erp.orders.delete(name);
    },
    onSuccess: () => {
      // Invalidate both detail and list queries
      queryClient.invalidateQueries({ queryKey: ["sales-order", name] });
      queryClient.invalidateQueries({ queryKey: ["sales-orders", "list"] });
      // Redirect to list page using route config
      navigate(routes.salesOrder.list);
    },
  });
}

/**
 * Hook for creating a Delivery Note (shipping an order).
 * Calls the whitelisted admin_fulfillment.create_delivery_note endpoint.
 */
export function useShipOrder(salesOrderName: string) {
  const queryClient = useQueryClient();

  return useMutation<
    { success: boolean; delivery_note: string; already_existed: boolean },
    Error,
    { trackingNumber?: string }
  >({
    mutationFn: async ({ trackingNumber }) => {
      const client = getERPClient();
      return client.call(
        "zaviago_backend.storefront.admin_fulfillment.create_delivery_note",
        {
          sales_order: salesOrderName,
          tracking_number: trackingNumber || "",
        }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sales-order", salesOrderName] });
      queryClient.invalidateQueries({ queryKey: ["sales-orders", "list"] });
      // Auto-invoice hook may create SI after DN submit — refetch linked invoice
      queryClient.invalidateQueries({ queryKey: ["linked-invoice", salesOrderName] });
    },
  });
}

/**
 * Item payload for partial shipment.
 */
interface PartialShipItem {
  item_code: string;
  qty: number;
  so_detail: string;
}

/**
 * Hook for creating a partial Delivery Note (shipping selected items).
 * Calls the whitelisted admin_fulfillment.create_partial_delivery_note endpoint.
 */
export function usePartialShipOrder(salesOrderName: string) {
  const queryClient = useQueryClient();

  return useMutation<
    { success: boolean; delivery_note: string; items_shipped: number },
    Error,
    { items: PartialShipItem[]; trackingNumber?: string }
  >({
    mutationFn: async ({ items, trackingNumber }) => {
      const client = getERPClient();
      return client.call(
        "zaviago_backend.storefront.admin_fulfillment.create_partial_delivery_note",
        {
          sales_order: salesOrderName,
          items: JSON.stringify(items),
          tracking_number: trackingNumber || "",
        }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sales-order", salesOrderName] });
      queryClient.invalidateQueries({ queryKey: ["sales-orders", "list"] });
      queryClient.invalidateQueries({ queryKey: ["linked-invoice", salesOrderName] });
    },
  });
}

/**
 * Hook to find the linked Sales Invoice for a Sales Order.
 * Returns the SI name (or null) by querying Sales Invoice Item child table.
 * Only fires when per_billed > 0 (enabled param controls this).
 */
export function useLinkedInvoice(
  salesOrderName: string,
  enabled: boolean
) {
  return useQuery<string | null>({
    queryKey: ["linked-invoice", salesOrderName],
    queryFn: async () => {
      try {
        const client = getERPClient();
        const items = await client.list({
          doctype: "Sales Invoice Item",
          filters: { sales_order: salesOrderName, docstatus: 1 },
          fields: ["parent"],
          limit_page_length: 1,
        });
        return items.length > 0 ? (items[0] as Record<string, string>).parent : null;
      } catch {
        // User may lack read permission on Sales Invoice Item — return null gracefully
        return null;
      }
    },
    enabled: enabled && !!salesOrderName,
    retry: false,
  });
}

/**
 * Hook for recording COD payment after delivery.
 * Calls the whitelisted admin_fulfillment.record_cod_payment endpoint.
 */
export function useRecordCODPayment(salesOrderName: string) {
  const queryClient = useQueryClient();

  return useMutation<
    { success: boolean; payment_entry: string },
    Error,
    void
  >({
    mutationFn: async () => {
      const client = getERPClient();
      return client.call(
        "zaviago_backend.storefront.admin_fulfillment.record_cod_payment",
        { sales_order: salesOrderName }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sales-order", salesOrderName] });
      queryClient.invalidateQueries({ queryKey: ["sales-orders", "list"] });
      // Auto-invoice hook may create SI after PE submit — refetch linked invoice
      queryClient.invalidateQueries({ queryKey: ["linked-invoice", salesOrderName] });
    },
  });
}

export function useRecordManualPayment(salesOrderName: string) {
  const queryClient = useQueryClient();

  return useMutation<
    { success: boolean; payment_entry: string },
    Error,
    {
      mode_of_payment: string;
      reference_no?: string;
      reference_date?: string;
      amount?: number;
    }
  >({
    mutationFn: async (args) => {
      const client = getERPClient();
      return client.call(
        "zaviago_backend.storefront.admin_payment.record_manual_payment",
        { sales_order: salesOrderName, ...args },
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sales-order", salesOrderName] });
      queryClient.invalidateQueries({ queryKey: ["sales-orders", "list"] });
      queryClient.invalidateQueries({ queryKey: ["linked-invoice", salesOrderName] });
    },
  });
}

// ── Refund (Plan 1929) ──────────────────────────────────────────────────────

export interface RefundEligibilityItem {
  sales_invoice_item: string;
  item_code: string;
  item_name: string;
  qty: number;
  refunded_qty: number;
  refundable_qty: number;
  rate: number;
  amount: number;
}

export interface RefundEligibility {
  sales_order: string;
  eligible: boolean;
  paid_status: string;
  paid_total: number;
  already_refunded: number;
  refundable: number;
  refund_status: string;
  currency: string;
  sales_invoice: string | null;
  items: RefundEligibilityItem[];
}

export interface RefundItemInput {
  sales_invoice_item: string;
  qty: number;
}

/**
 * Fetch refund eligibility (refundable amount + per-item breakdown) for the
 * Refund dialog. Only fires when the dialog is open (enabled).
 */
export function useRefundEligibility(salesOrderName: string, enabled: boolean) {
  return useQuery<RefundEligibility>({
    queryKey: ["refund-eligibility", salesOrderName],
    queryFn: async () => {
      const client = getERPClient();
      return client.call(
        "zaviago_backend.storefront.admin_refund.get_refund_eligibility",
        { sales_order: salesOrderName },
      );
    },
    enabled: enabled && !!salesOrderName,
    staleTime: 0,
  });
}

/**
 * Issue a refund (Credit Note + refund Payment Entry) for a paid order.
 * Modes (precedence): refund_items (per-item) > amount (custom) > full.
 */
export function useRefundOrder(salesOrderName: string) {
  const queryClient = useQueryClient();

  return useMutation<
    {
      success: boolean;
      credit_note: string;
      refund_entry: string;
      refunded_amount: number;
      refund_status: string;
      refundable_remaining: number;
    },
    Error,
    {
      reason: string;
      amount?: number;
      refund_items?: RefundItemInput[];
      mode_of_payment?: string;
      reference_no?: string;
      reference_date?: string;
    }
  >({
    mutationFn: async (args) => {
      const client = getERPClient();
      const payload: Record<string, unknown> = {
        sales_order: salesOrderName,
        reason: args.reason,
      };
      if (args.refund_items && args.refund_items.length) {
        payload.refund_items = JSON.stringify(args.refund_items);
      } else if (args.amount != null) {
        payload.amount = args.amount;
      }
      if (args.mode_of_payment) payload.mode_of_payment = args.mode_of_payment;
      if (args.reference_no) payload.reference_no = args.reference_no;
      if (args.reference_date) payload.reference_date = args.reference_date;
      return client.call(
        "zaviago_backend.storefront.admin_refund.issue_refund",
        payload,
      );
    },
    onSuccess: () => {
      // ["get","Sales Order",name] powers the /orders?detail= slide-over panel — MUST invalidate.
      queryClient.invalidateQueries({ queryKey: ["get", "Sales Order", salesOrderName] });
      queryClient.invalidateQueries({ queryKey: ["sales-order", salesOrderName] });
      queryClient.invalidateQueries({ queryKey: ["orders", "detail", salesOrderName] });
      queryClient.invalidateQueries({ queryKey: ["sales-orders", "list"] });
      queryClient.invalidateQueries({ queryKey: ["orders", "list"] });
      queryClient.invalidateQueries({ queryKey: ["linked-invoice", salesOrderName] });
      queryClient.invalidateQueries({ queryKey: ["refund-eligibility", salesOrderName] });
    },
  });
}

